import type { Disposable } from '../../types/disposable';
import type { Rpc } from '../rpc';

/**
 * RPC 实现类
 */
export class RpcImpl<Methods extends Record<string, (...args: any[]) => any>> implements Rpc<Methods> {
    private handlers: Map<keyof Methods, Methods[keyof Methods]> = new Map();

    call<M extends keyof Methods>(
        method: M,
        ...args: Parameters<Methods[M]>
    ): ReturnType<Methods[M]> {
        const handler = this.handlers.get(method) as Methods[M] | undefined;
        if (!handler) {
            throw new Error(`Method ${String(method)} not found`);
        }
        return handler(...(args as any[]));
    }

    register<M extends keyof Methods>(
        method: M,
        handler: Methods[M],
    ): Disposable {
        if (this.handlers.has(method)) {
            throw new Error(`Method ${String(method)} already registered`);
        }
        this.handlers.set(method, handler);

        return {
            dispose: () => {
                this.unregister(method);
            },
        };
    }

    unregister(method: keyof Methods): void {
        this.handlers.delete(method);
    }

    clear(): void {
        this.handlers.clear();
    }

    isRegistered(method: keyof Methods): boolean {
        return this.handlers.has(method);
    }
}
